/**
 * Helpers shared by the "progress" rule ops (ops/progress-*.ts): line-by-line ports of the FWP / new_interf methods
 * several of those ops need (achievement and task bonuses, copy counting, calendar comparisons).
 *
 * Every game function is ported once in src/game/rules: the progress ops import FWP.check_new_level from
 * ./battle-level.ts, FWP.get_perk_effect from ./battle-formulas.ts, FWP.modify_tired / modify_papa / modify_wins
 * from ./battle-time.ts and FWP.get_achiev_level / get_achiev_rank / weapon_optimisation from ./shop-common.ts.
 *
 * Browser-safe like the rest of src/game/rules (imports only ../*): see ../index.ts.
 */
import { int, keys } from '../as3';
import type { RuleContext } from '../context';
import { playerLevel, type GameDefs } from '../defs';
import type { PlayerState } from '../state';
import type { EndGame } from './battle-level';
import { achievLevel, achievRank } from './shop-common';

// ------------------------------------------------------------------------------------------------ level

/**
 * The stale-level fix (FWP.net_initialized, FWP.ts:2352 `player.level.value = get_player_level(exp)`): the stored
 * `level` can lag behind `exp` because the original check_new_level never updates it (only new_interf.init_new_level
 * does, after the save). Ops that read the level derive it from exp first, exactly as every session start does.
 */
export function syncLevel(p: PlayerState, defs: Pick<GameDefs, 'LEVEL_THRESHOLD'>): void {
  p.level = int(playerLevel(defs, p.exp));
}

/** FWP.get_attack_cost (FWP.ts:5999): skill points one more point of attack/protect costs at `v`. */
export function attackCost(v: number): number {
  v = Number(v);
  if (v < 100) return 1;
  if (v < 150) return 2;
  if (v < 200) return 3;
  if (v < 250) return 4;
  return 5;
}

/** What a level-up inside a claim gave (FWP.end_game after check_new_level). */
export interface LevelUpResult {
  levels: number;
  skill_pts: number;
  perk_pts: number;
  diam: number;
  /** Weapons of the surprise perk, one per level. */
  surpriseWeapons: string[];
}

export function levelUpResult(e: EndGame): LevelUpResult {
  const surprise: string[] = [];
  for (let i = 1; i <= e.surprise_weapons[0]; i++) surprise.push(String(e.surprise_weapons[i]));
  return { levels: e.levels_upg, skill_pts: e.skill_pts, perk_pts: e.perk_pts, diam: e.diam_bonus, surpriseWeapons: surprise };
}

// ------------------------------------------------------------------------------------------------ achievements

export interface Bonus {
  exist: boolean;
  gold: number;
  diam: number;
  exp: number;
  rating: number;
  [prise: string]: any;
}

/**
 * FWP.get_achiev_bonus (FWP.ts:5929-5978), including its side effect: it rewrites the derived counters
 * achiev.recipes (known recipes) and achiev.career/missions/survival/boss (*_max - 1) first.
 */
export function achievBonus(p: PlayerState, defs: GameDefs): Bonus {
  const out: Bonus = { exist: false, gold: 0, diam: 0, exp: 0, rating: 0 };
  let known = 0;
  for (let i = 1; i <= defs.CRAFT_DEF[0]; i++) if (p.recipes[i] == 1) known++;
  p.achiev['recipes'] = known;
  p.achiev['career'] = p.career_max - 1;
  p.achiev['missions'] = p.missions_max - 1;
  p.achiev['survival'] = p.survival_max - 1;
  p.achiev['boss'] = p.boss_max - 1;
  for (const k of keys(p.achiev)) {
    const key = k as string;
    const level = achievLevel(defs, key, p.achiev[key]);
    if (p.last_achiev[key] != level) {
      if (p.last_achiev[key] < level) {
        let guard = 0;
        let l = p.last_achiev[key];
        do {
          if (guard >= 20) break;
          guard++;
          l++;
          out[defs.ACHIEV_PRISE_DEF[key].prise] += defs.ACHIEV_PRISE_DEF[key].amount[l];
          out.exist = true;
        } while (l != achievLevel(defs, key, p.achiev[key]));
      }
    }
  }
  if (achievRank(defs, p.achiev) != p.last_achiev_range) {
    out.rating = defs.ACHIEV_RATING_BONUS[achievRank(defs, p.achiev)];
    out.exist = true;
  }
  return out;
}

/** FWP.get_task_bonus (FWP.ts:5980): the bonuses of tasks last_presented_task .. cur_task - 1. */
export function taskBonus(p: PlayerState, defs: GameDefs): Bonus {
  const out: Bonus = { exist: false, gold: 0, diam: 0, exp: 0, rating: 0 };
  for (let t = p.last_presented_task; t <= p.cur_task - 1; t++) {
    out[defs.TASK_DEF[t].bonus_type] += defs.TASK_DEF[t].bonus;
    out.exist = true;
  }
  return out;
}

// ------------------------------------------------------------------------------------------------ weapons

/** Number of copies of weapon `id` that match (range, stolen, crafted) exactly (the handlers' `==` comparison). */
export function countCopies(p: PlayerState, id: string, range: number, stolen: boolean, crafted: boolean): number {
  const list = p.weapons[id];
  if (!list) return 0;
  let n = 0;
  for (let i = 1; i <= list[0]; i++) {
    const c = list[i];
    if (c && c.range == range && c.stolen == stolen && c.crafted == crafted) n++;
  }
  return n;
}

// ------------------------------------------------------------------------------------------------ calendar

/** Day / month (1-based, like getMonth() + 1) / year of `unix` in the player's calendar. */
export function dayOf(ctx: RuleContext, unix: number): { date: number; month: number; year: number; hours: number } {
  const c = ctx.calendar(unix);
  return { date: c.date, month: c.month + 1, year: c.year, hours: c.hours };
}

/**
 * The "yesterday" test of modify_last_enter / modify_wins (FWP.ts:4800, 4879): |day difference| == 1, or the 1st
 * of a month after the previous month, or January 1st after the previous year (the original's own approximation,
 * kept as the rule).
 */
export function isNextDay(now: { date: number; month: number; year: number }, then: { date: number; month: number; year: number }): boolean {
  return Math.abs(now.date - then.date) == 1 || (now.date == 1 && Math.abs(now.month - then.month) == 1) || (now.date == 1 && now.month == 1 && Math.abs(now.year - then.year) == 1);
}

export function sameDay(a: { date: number; month: number; year: number }, b: { date: number; month: number; year: number }): boolean {
  return !(a.date != b.date || a.month != b.month || a.year != b.year);
}
